import type { ElementType, ReactNode } from "react";
import {
  IconAlertTriangle,
  IconExclamationCircle,
  IconInfoCircle,
  IconCircleCheck,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import styles from "./Callout.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Title from "./Title.tsx";
import Text from "./Text.tsx";
import IconButton from "./IconButton.tsx";

export type CalloutVariant =
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "neutral"
  | "violet";

const DEFAULT_ICONS: Record<CalloutVariant, ElementType> = {
  info: IconInfoCircle,
  success: IconCircleCheck,
  warning: IconAlertTriangle,
  danger: IconExclamationCircle,
  neutral: IconInfoCircle,
  violet: IconSparkles,
};

// warning / danger portent une information à traiter → role="alert" (annonce
// immédiate), les autres se contentent de role="status" (annonce polie).
const ALERT_VARIANTS: ReadonlySet<CalloutVariant> = new Set([
  "warning",
  "danger",
]);

interface CalloutProps {
  /** Couleur et ton du message. Défaut : "info". */
  variant?: CalloutVariant;
  /** Titre optionnel en gras au-dessus du corps. */
  title?: ReactNode;
  /**
   * Corps du message. Si `children`, `title` et `action` sont tous vides, le
   * composant ne rend rien — inutile de garder un `{message && <Callout…>}`
   * côté appelant, `<Callout>{message}</Callout>` suffit.
   */
  children?: ReactNode;
  /**
   * Icône à afficher à gauche. Par défaut, l'icône associée à la `variant`.
   * Passer `null` pour ne pas afficher d'icône.
   */
  icon?: ElementType | null;
  /** Si fourni, affiche une croix de fermeture qui appelle ce callback. */
  onClose?: () => void;
  /** Zone d'action optionnelle sous le corps (boutons, liens…). */
  action?: ReactNode;
  className?: string;
}

export default function Callout({
  variant = "info",
  title,
  children,
  icon,
  onClose,
  action,
  className,
}: CalloutProps) {
  // Rien à afficher (message vide/absent, pas de titre ni d'action) → on ne
  // rend rien, ce qui évite au code appelant de garder un `{message && …}`.
  if (!children && !title && !action) {
    return null;
  }

  const Icon = icon === null ? null : icon ?? DEFAULT_ICONS[variant];

  return (
    <div
      className={classnames(styles.callout, styles[variant], className)}
      role={ALERT_VARIANTS.has(variant) ? "alert" : "status"}
    >
      {Icon && <Icon className={styles.icon} size={22} />}
      <div className={styles.content}>
        {title && (
          <Title as="div" variant="deca" weight="bold">
            {title}
          </Title>
        )}
        {children && <Text as="div">{children}</Text>}
        {action && <div className={styles.action}>{action}</div>}
      </div>
      {onClose && (
        <IconButton
          icon={IconX}
          label="Fermer"
          variant="plain"
          size="sm"
          onClick={onClose}
          className={styles.close}
        />
      )}
    </div>
  );
}
